/**
 * Toucan Reads — save a link to read later. This boots the app. It opens the
 * store and takes in anything the share sheet arrived with, then renders the
 * shell, and `Shell.tsx` takes it from there.
 */

import { render } from "preact";
import { flushBlobs, isStandaloneApp, required, toucan } from "toucan";
import { Shell } from "./Shell.tsx";
import { saveUrl, sharedLink } from "./model.ts";
import { rememberReading, storedList, storedReading } from "./remembered.ts";
import { listPath, noteInterruptedReading, noteJustSaved } from "./routes.ts";
import { lists, setApp, store } from "./state.ts";
import type { Item, List } from "./model.ts";

/**
 * Take in a link the share sheet launched the app with, and open it.
 *
 * The history entry is *replaced* rather than pushed, so a back gesture leaves
 * the app instead of returning to the address that shared the link — which
 * would save it a second time.
 */
function takeShare(): void {
  const shared = sharedLink(location.search);
  if (shared === null) {
    return;
  }
  const saved = required(
    saveUrl(store(), shared.url, Date.now(), shared.title),
    "saved share",
  );
  if (saved.created) {
    noteJustSaved(saved.id);
  }
  // A share over the reader relaunches the installed app; the reading it
  // interrupted is where leaving the new item's page should go. In a tab the
  // share is the bookmarklet, which interrupts nothing, and a stored reading
  // is from another window.
  const reading = isStandaloneApp() ? storedReading() : undefined;
  if (reading !== undefined) {
    noteInterruptedReading(reading);
  }
  history.replaceState(null, "", `${location.pathname}#/edit/${saved.id}`);
}

/**
 * Open on the list this browser was last reading from, when the address names
 * no page. The entry is replaced rather than pushed, so a back gesture leaves
 * the app.
 */
function openRemembered(): void {
  if (location.hash !== "" && location.hash !== "#/") {
    return;
  }
  const path = listPath(storedList(lists()));
  // The whole library is what no address shows anyway.
  if (path === "") {
    return;
  }
  history.replaceState(null, "", `${location.pathname}#/${path}`);
}

/**
 * Open the store and put the app on the page — the whole of what loading this
 * module does, and the whole of what a browser needs from it.
 *
 * Exported for the tests, which boot the app once per test into a jsdom the
 * files in a worker share. A boot that is only a module's evaluation happens
 * once per module instance, and no test can insist on being given a new one:
 * see `boot()` in `test-helpers.ts` for what the shared registry does with
 * that.
 */
export async function main() {
  setApp(
    await toucan.open("reads", {
      lists: [] as List[],
      items: [] as Item[],
    }),
  );
  takeShare();
  // `takeShare` has already read the stored reading, and any other launch is
  // not an interruption of it.
  rememberReading(undefined);
  openRemembered();
  render(<Shell />, document.body);

  // An article's text and a PDF's first page are blobs, which the document
  // holds only by hash, so upload any still on this device alone. The store
  // makes the same pass whenever the device wakes or comes back online; this
  // is the one at boot.
  void flushBlobs();
}

export const ready = main();
